import { useCallback, useEffect, useRef, useState } from 'react';
import { BookOpen, CaretDown, Gear, Lightbulb, MagnifyingGlass, PencilSimple, Plus, Trash } from 'shared/icons';
import api, { GetToken } from '../../services/api';
import { ResolveUploadsBaseUrl } from '../../utils/assetUrl';
import { FormatDateTimeBr } from '../../utils/date';
import Button from '../ui/Button';
import LoadingSpinner from '../ui/LoadingSpinner';
import Modal from '../ui/Modal';

// ── Fundações visuais do Atendimento ─────────────────────────────────────────
// Redesign 2026-08-27, mesma filosofia do Pós-venda: hierarquia CALMA. A fila
// é UMA caixa priorizada por tempo de espera; vermelho só quando o comprador
// espera demais. O detalhe abre em camadas: contexto do produto (base de
// conhecimento) + thread + editor.

export const MESSAGE_MAX_LENGTH = 350; // limite do ML no chat pós-venda
export const QUESTION_MAX_ANSWER_LENGTH = 2000; // limite do ML nas perguntas
export const QUICK_REPLY_BODY_MAX = 2000;
export const KNOWLEDGE_CONTENT_MAX = 8000;

export const QUESTION_STATUS_META = {
    UNANSWERED: {
        label: 'Aguardando',
        pill: 'bg-warning-50 text-warning-800 ring-warning-600/20 dark:bg-warning-400/10 dark:text-warning-300 dark:ring-warning-400/30',
    },
    ANSWERED: {
        label: 'Respondida',
        pill: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/30',
    },
    CLOSED_UNANSWERED: {
        label: 'Fechada sem resposta',
        pill: 'bg-mono-100 text-mono-500 ring-mono-400/20 dark:bg-mono-900 dark:text-mono-400 dark:ring-mono-600/30',
    },
    UNDER_REVIEW: {
        label: 'Em revisão (ML)',
        pill: 'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-400/10 dark:text-sky-300 dark:ring-sky-400/30',
    },
    BANNED: {
        label: 'Banida (ML)',
        pill: 'bg-mono-100 text-mono-500 ring-mono-400/20 dark:bg-mono-900 dark:text-mono-400 dark:ring-mono-600/30',
    },
    DELETED: {
        label: 'Apagada',
        pill: 'bg-mono-100 text-mono-500 ring-mono-400/20 dark:bg-mono-900 dark:text-mono-400 dark:ring-mono-600/30',
    },
    DISABLED: {
        label: 'Desativada',
        pill: 'bg-mono-100 text-mono-500 ring-mono-400/20 dark:bg-mono-900 dark:text-mono-400 dark:ring-mono-600/30',
    },
};

/** Pill de status da pergunta — status desconhecido aparece CRU em pill neutra. */
export const QuestionStatusPill = ({ status }) => {
    const meta = QUESTION_STATUS_META[status] || {
        label: status || '—',
        pill: 'bg-mono-100 text-mono-500 ring-mono-400/20 dark:bg-mono-900 dark:text-mono-400 dark:ring-mono-600/30',
    };
    return (
        <span
            className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${meta.pill}`}
        >
            {meta.label}
        </span>
    );
};

/**
 * Quanto tempo o comprador está esperando — o "prazo" do atendimento. No ML,
 * responder rápido é reputação: >24h é vermelho, >6h é alerta.
 */
export function WaitingInfo(iso) {
    if (!iso) {
        return null;
    }
    const diffMs = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(diffMs) || diffMs < 0) {
        return null;
    }
    const hours = diffMs / 3600_000;
    const human =
        hours < 1
            ? `${Math.max(1, Math.round(hours * 60))}min`
            : hours < 24
              ? `${Math.round(hours)}h`
              : `${Math.floor(hours / 24)}d`;
    if (hours >= 24) {
        return { label: `há ${human}`, tone: 'text-danger-600 dark:text-danger-400', breached: true };
    }
    if (hours >= 6) {
        return { label: `há ${human}`, tone: 'text-warning-text dark:text-warning-300', breached: false };
    }
    return { label: `há ${human}`, tone: 'text-mono-500 dark:text-mono-400', breached: false };
}

/** Tag do tipo de item na caixa unificada: pergunta pré-venda × chat pós-venda. */
export const KindTag = ({ kind }) => (
    <span
        className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
            kind === 'question'
                ? 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-600/20 dark:bg-violet-400/10 dark:text-violet-300 dark:ring-violet-400/30'
                : 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-600/20 dark:bg-sky-400/10 dark:text-sky-300 dark:ring-sky-400/30'
        }`}
    >
        {kind === 'question' ? 'Pergunta' : 'Chat'}
    </span>
);

// ── Anexos do chat ───────────────────────────────────────────────────────────

// Anexo (foto/arquivo) que o comprador mandou — servido pelo backend como proxy
// do ML (o browser não manda o header Authorization, então o token vai por
// query, mesmo padrão dos claim-attachments do RMA).
export function ConversationAttachmentUrl(conversationId, filename) {
    const token = GetToken();
    const base = ResolveUploadsBaseUrl();
    const query = token ? `?access_token=${token}` : '';
    return `${base}/atendimento/conversations/${conversationId}/attachments/${encodeURIComponent(filename)}${query}`;
}

export function MessageAttachments({ conversationId, attachments }) {
    if (!Array.isArray(attachments) || attachments.length === 0) {
        return null;
    }
    return (
        <div className="mt-2 space-y-2">
            {attachments.map((attachment) => {
                const url = ConversationAttachmentUrl(conversationId, attachment.filename);
                const isImage = String(attachment.type || '').startsWith('image/');
                const label = attachment.original_filename || attachment.filename;
                return (
                    <a
                        key={attachment.filename}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                        title={label}
                    >
                        {isImage ? (
                            <img
                                src={url}
                                alt={label || 'anexo'}
                                loading="lazy"
                                className="max-h-56 max-w-full rounded-lg border border-mono-200 dark:border-mono-800"
                            />
                        ) : (
                            <span className="inline-flex items-center gap-1 break-all text-xs underline">
                                📎 {label}
                            </span>
                        )}
                    </a>
                );
            })}
        </div>
    );
}

// ── Thread da conversa (balões, padrão do chat de mediação) ──────────────────

export const ConversationThread = ({ conversation, messages, maxHeightClass = 'max-h-[46vh]' }) => {
    const endRef = useRef(null);
    useEffect(() => {
        endRef.current?.scrollIntoView({ block: 'nearest' });
    }, [messages]);

    if (!messages?.length) {
        return <p className="py-6 text-center text-sm text-mono-400">Sem mensagens ainda — mande a primeira.</p>;
    }
    return (
        <div
            className={`flex ${maxHeightClass} flex-col gap-3 overflow-y-auto rounded-xl bg-mono-50 p-4 dark:bg-mono-950`}
        >
            {messages.map((message) => {
                const ours = message.sender_role === 'seller';
                const hasBody = Boolean(message.message_text);
                const hasAttachments = Array.isArray(message.attachments) && message.attachments.length > 0;
                return (
                    <div key={message.id} className={`flex flex-col ${ours ? 'items-end' : 'items-start'}`}>
                        <span className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wide text-mono-400 dark:text-mono-600">
                            {ours ? 'Nós' : 'Comprador'} · {FormatDateTimeBr(message.message_date, { blank: '—' })}
                        </span>
                        <div
                            className={`max-w-[80%] whitespace-pre-wrap px-4 py-2.5 text-sm leading-relaxed ${
                                ours
                                    ? 'rounded-2xl rounded-br-md bg-black text-white dark:bg-white dark:text-black'
                                    : 'rounded-2xl rounded-bl-md bg-white text-mono-900 ring-1 ring-mono-200 dark:bg-mono-900 dark:text-mono-100 dark:ring-mono-800'
                            }`}
                        >
                            {hasBody ? message.message_text : null}
                            <MessageAttachments conversationId={conversation.id} attachments={message.attachments} />
                            {!hasBody && !hasAttachments ? <span className="italic opacity-70">(sem conteúdo)</span> : null}
                            {message.message_status && message.message_status !== 'available' ? (
                                <p className="mt-1 text-[10px] italic opacity-60">
                                    {message.message_status === 'rejected' ? 'rejeitada pelo ML' : 'moderada pelo ML'}
                                </p>
                            ) : null}
                        </div>
                    </div>
                );
            })}
            <div ref={endRef} />
        </div>
    );
};

// ── Respostas rápidas ────────────────────────────────────────────────────────

// Menu de respostas rápidas: escolher COLA o corpo no input (o operador ainda
// revisa e clica em Enviar — nada é enviado automático). Abre pra cima
// (bottom-full) e alinhado à direita porque vive no rodapé do editor.
export function QuickReplyMenu({ quickReplies, onPick, disabled }) {
    const [open, setOpen] = useState(false);
    const hasReplies = Array.isArray(quickReplies) && quickReplies.length > 0;
    return (
        <div className="relative">
            <Button
                variant="secondary"
                size="sm"
                disabled={disabled}
                onClick={() => setOpen((value) => !value)}
                title="Inserir uma resposta rápida no texto"
            >
                Respostas rápidas
                <CaretDown size={14} className="ml-1" />
            </Button>
            {open ? (
                <>
                    <button
                        type="button"
                        aria-label="Fechar menu de respostas rápidas"
                        className="fixed inset-0 z-10 cursor-default"
                        onClick={() => setOpen(false)}
                    />
                    <div className="absolute bottom-full right-0 z-20 mb-1 max-h-72 w-80 overflow-y-auto rounded-lg border border-mono-200 bg-white p-1 shadow-lg dark:border-mono-800 dark:bg-mono-950">
                        {hasReplies ? (
                            quickReplies.map((quickReply) => (
                                <button
                                    key={quickReply.id}
                                    type="button"
                                    onClick={() => {
                                        onPick(quickReply.body);
                                        setOpen(false);
                                    }}
                                    className="block w-full rounded-md px-3 py-2 text-left hover:bg-mono-100 dark:hover:bg-mono-900"
                                >
                                    <span className="block text-sm font-medium text-black dark:text-white">
                                        {quickReply.name}
                                    </span>
                                    {quickReply.description ? (
                                        <span className="block truncate text-xs text-mono-500 dark:text-mono-400">
                                            {quickReply.description}
                                        </span>
                                    ) : null}
                                    <span className="mt-0.5 line-clamp-2 block text-xs text-mono-400">
                                        {quickReply.body}
                                    </span>
                                </button>
                            ))
                        ) : (
                            <p className="px-3 py-2 text-xs text-mono-500">Nenhuma resposta rápida cadastrada ainda.</p>
                        )}
                    </div>
                </>
            ) : null}
        </div>
    );
}

const INPUT_CLASS =
    'w-full rounded-lg border border-mono-200 bg-white px-3 py-2 text-sm text-black outline-none focus:border-mono-400 dark:border-mono-800 dark:bg-mono-950 dark:text-white';

const EMPTY_QUICK_REPLY = { name: '', description: '', body: '' };

// Gestão (CRUD) das respostas rápidas — só para quem tem
// `atendimento:quick_replies:manage`. Lista + formulário criar/editar + exclusão
// com confirmação inline (o design system evita window.confirm).
export function QuickRepliesManager({ isOpen, onClose, quickReplies, onChanged }) {
    const [editing, setEditing] = useState(null); // null = lista; {} = novo; {id} = edição
    const [form, setForm] = useState(EMPTY_QUICK_REPLY);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [confirmingId, setConfirmingId] = useState(null);

    const backToList = () => {
        setEditing(null);
        setFormError('');
    };

    const startNew = () => {
        setForm(EMPTY_QUICK_REPLY);
        setEditing({});
        setFormError('');
    };

    const startEdit = (quickReply) => {
        setForm({
            name: quickReply.name || '',
            description: quickReply.description || '',
            body: quickReply.body || '',
        });
        setEditing(quickReply);
        setFormError('');
    };

    const handleSave = async () => {
        const name = form.name.trim();
        const body = form.body.trim();
        if (!name || !body) {
            setFormError('Nome e mensagem são obrigatórios.');
            return;
        }
        try {
            setSaving(true);
            setFormError('');
            const payload = { name, description: form.description.trim(), body };
            if (editing?.id) {
                await api.updateAtendimentoQuickReply(editing.id, payload);
            } else {
                await api.createAtendimentoQuickReply(payload);
            }
            await onChanged();
            backToList();
        } catch (error) {
            setFormError(error.message || 'Falha ao salvar a resposta rápida.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (quickReply) => {
        try {
            setSaving(true);
            setFormError('');
            await api.deleteAtendimentoQuickReply(quickReply.id);
            setConfirmingId(null);
            await onChanged();
        } catch (error) {
            setFormError(error.message || 'Falha ao excluir a resposta rápida.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Respostas rápidas" size="2xl" icon={<Gear size={20} />}>
            {editing ? (
                <div className="space-y-3">
                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-mono-600 dark:text-mono-300">Nome</span>
                        <input
                            type="text"
                            value={form.name}
                            maxLength={120}
                            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                            placeholder="Ex.: Saudação inicial"
                            className={INPUT_CLASS}
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-mono-600 dark:text-mono-300">
                            Quando usar (opcional)
                        </span>
                        <input
                            type="text"
                            value={form.description}
                            maxLength={300}
                            onChange={(event) =>
                                setForm((current) => ({ ...current, description: event.target.value }))
                            }
                            placeholder="Ex.: Primeira resposta ao comprador"
                            className={INPUT_CLASS}
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-mono-600 dark:text-mono-300">
                            Mensagem
                        </span>
                        <textarea
                            value={form.body}
                            rows={5}
                            maxLength={QUICK_REPLY_BODY_MAX}
                            onChange={(event) => setForm((current) => ({ ...current, body: event.target.value }))}
                            placeholder="Corpo que será colado no input do chat"
                            className={INPUT_CLASS}
                        />
                        <span className="text-xs text-mono-400">
                            {form.body.length}/{QUICK_REPLY_BODY_MAX}
                        </span>
                    </label>
                    {formError ? <p className="text-sm text-red-600 dark:text-red-400">{formError}</p> : null}
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={backToList} disabled={saving}>
                            Cancelar
                        </Button>
                        <Button onClick={handleSave} disabled={saving || !form.name.trim() || !form.body.trim()}>
                            {saving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="flex justify-end">
                        <Button size="sm" onClick={startNew}>
                            <Plus size={16} className="mr-1" />
                            Nova resposta
                        </Button>
                    </div>
                    {formError ? <p className="text-sm text-red-600 dark:text-red-400">{formError}</p> : null}
                    {!quickReplies || quickReplies.length === 0 ? (
                        <p className="py-6 text-center text-sm text-mono-500">
                            Nenhuma resposta rápida cadastrada. Crie a primeira.
                        </p>
                    ) : (
                        <ul className="divide-y divide-mono-100 dark:divide-mono-900">
                            {quickReplies.map((quickReply) => (
                                <li key={quickReply.id} className="flex items-start justify-between gap-3 py-3">
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-black dark:text-white">
                                            {quickReply.name}
                                        </p>
                                        {quickReply.description ? (
                                            <p className="text-xs text-mono-500 dark:text-mono-400">
                                                {quickReply.description}
                                            </p>
                                        ) : null}
                                        <p className="mt-0.5 line-clamp-2 text-xs text-mono-400">{quickReply.body}</p>
                                    </div>
                                    {confirmingId === quickReply.id ? (
                                        <div className="flex shrink-0 items-center gap-1">
                                            <span className="text-xs text-mono-500">Excluir?</span>
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleDelete(quickReply)}
                                                disabled={saving}
                                            >
                                                Sim
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => setConfirmingId(null)}
                                                disabled={saving}
                                            >
                                                Não
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="flex shrink-0 items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => startEdit(quickReply)}
                                                title="Editar"
                                            >
                                                <PencilSimple size={16} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setConfirmingId(quickReply.id)}
                                                title="Excluir"
                                            >
                                                <Trash size={16} />
                                            </Button>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </Modal>
    );
}

// ── Editor de resposta (compartilhado por pergunta e chat) ───────────────────

/**
 * Textarea + contador + respostas rápidas + sugestão de IA + enviar. O envio é
 * SEMPRE manual: IA e templates só preenchem o texto para o operador revisar.
 */
export const ReplyComposer = ({
    value,
    onChange,
    maxLength,
    rows = 3,
    placeholder,
    quickReplies,
    onPickQuickReply,
    aiEnabled,
    aiProvider = null,
    suggesting,
    onSuggest,
    suggestionAt = null,
    sending,
    onSend,
    sendLabel = 'Enviar',
}) => {
    const nearLimit = value.length > maxLength - 30;
    const aiTitle = aiEnabled
        ? `Gerar sugestão com IA${aiProvider ? ` (${aiProvider})` : ''} usando a base de conhecimento do produto`
        : 'Configure GEMINI_API_KEY (ou ANTHROPIC_API_KEY) no .env do backend';
    return (
        <div className="space-y-2">
            <textarea
                value={value}
                onChange={(event) => onChange(event.target.value)}
                rows={rows}
                maxLength={maxLength}
                placeholder={placeholder}
                className="w-full rounded-lg border border-mono-200 bg-white p-3 text-sm text-black outline-none focus:border-mono-400 dark:border-mono-800 dark:bg-mono-950 dark:text-white"
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
                <span className={`text-xs ${nearLimit ? 'font-medium text-amber-600' : 'text-mono-400'}`}>
                    {value.length}/{maxLength}
                    {suggestionAt ? ` · sugestão de ${FormatDateTimeBr(suggestionAt, { blank: '—' })}` : ''}
                </span>
                <div className="flex items-center gap-2">
                    <QuickReplyMenu quickReplies={quickReplies} onPick={onPickQuickReply} disabled={sending} />
                    <Button
                        variant="secondary"
                        onClick={onSuggest}
                        disabled={!aiEnabled || suggesting || sending}
                        title={aiTitle}
                    >
                        <Lightbulb size={16} className="mr-2" />
                        {suggesting ? 'Gerando...' : 'Sugerir com IA'}
                    </Button>
                    <Button onClick={onSend} disabled={sending || !value.trim()}>
                        {sending ? 'Enviando...' : sendLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ── Base de conhecimento (painel de contexto + gestão) ───────────────────────

// Campos do documento de produto — [chave wire, rótulo, dica]. `internal_notes`
// é interno: aparece para o atendente com a marcação "interno" e NÃO vai à IA.
export const PRODUCT_FORM_SECTIONS = [
    ['specs', 'Ficha técnica', 'Voltagem, medidas, peso, material…'],
    ['package_contents', 'O que acompanha', 'Conteúdo da embalagem'],
    ['compatibility', 'Compatibilidade', 'Modelos/aparelhos/veículos compatíveis'],
    ['warranty_info', 'Garantia', 'Prazo e como acionar'],
    ['shipping_notes', 'Envio e prazo', 'Despacho, transportadora, embalagem'],
    ['usage_notes', 'Instalação e uso', 'Dicas, cuidados, passo a passo'],
    ['faq', 'Perguntas frequentes', 'P: … / R: … (uma por linha)'],
    ['internal_notes', 'Notas internas', 'Só o atendente vê — NÃO vai para a IA'],
];

/**
 * A camada de contexto do detalhe: o DOCUMENTO do SKU (cadastro estruturado) +
 * fichas livres + políticas gerais — o que o atendente precisa saber antes de
 * responder, e o mesmo conteúdo que ancora a sugestão de IA (menos as notas
 * internas, que ficam só aqui).
 */
export const KnowledgePanel = ({
    product,
    entries,
    loading,
    canManage,
    targetSku,
    onEditProduct,
    onCreateFicha,
    onEditFicha,
}) => {
    const [expandedId, setExpandedId] = useState(null);
    const filledSections = product
        ? PRODUCT_FORM_SECTIONS.filter(([key]) => String(product[key] || '').trim())
        : [];
    return (
        <section className="rounded-xl border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950">
            <header className="flex items-center justify-between gap-2 px-4 pb-1 pt-3">
                <h3 className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                    <BookOpen size={14} />
                    Conhecimento do produto
                    {targetSku ? (
                        <span className="rounded-full bg-mono-100 px-1.5 font-mono text-[10px] font-semibold normal-case tracking-normal text-mono-500 dark:bg-mono-900 dark:text-mono-400">
                            SKU {targetSku}
                        </span>
                    ) : null}
                </h3>
                <span className="flex items-center gap-1">
                    {canManage && targetSku ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onEditProduct}
                            // Enquanto o contexto carrega não dá para saber se o SKU
                            // já tem documento — abrir agora arriscaria editar às cegas.
                            disabled={loading}
                            title={product ? 'Editar o documento deste SKU' : 'Cadastrar o documento deste SKU'}
                        >
                            <PencilSimple size={14} className="mr-1" />
                            Documento
                        </Button>
                    ) : null}
                    {canManage ? (
                        <Button variant="ghost" size="sm" onClick={onCreateFicha} title="Nova ficha avulsa">
                            <Plus size={14} className="mr-1" />
                            Ficha
                        </Button>
                    ) : null}
                </span>
            </header>
            <div className="px-4 pb-3">
                {loading ? (
                    <p className="py-2 text-xs text-mono-400">Carregando contexto do produto...</p>
                ) : (
                    <>
                        {/* Documento do SKU */}
                        {product ? (
                            <div className="pb-1">
                                <p className="text-sm font-medium text-black dark:text-white">
                                    {product.name}
                                    {product.brand || product.model ? (
                                        <span className="ml-1.5 text-xs font-normal text-mono-500 dark:text-mono-400">
                                            {[product.brand, product.model].filter(Boolean).join(' · ')}
                                        </span>
                                    ) : null}
                                </p>
                                {filledSections.length === 0 ? (
                                    <p className="py-1 text-xs text-mono-400">
                                        Documento sem seções preenchidas ainda.
                                    </p>
                                ) : (
                                    <ul className="divide-y divide-mono-100 dark:divide-mono-900">
                                        {filledSections.map(([key, label]) => {
                                            const expanded = expandedId === `doc:${key}`;
                                            return (
                                                <li key={key} className="py-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setExpandedId(expanded ? null : `doc:${key}`)
                                                        }
                                                        className="block w-full text-left"
                                                        title={expanded ? 'Recolher' : 'Expandir'}
                                                    >
                                                        <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                                                            {label}
                                                            {key === 'internal_notes' ? (
                                                                <span className="rounded-full bg-warning-50 px-1.5 font-semibold normal-case tracking-normal text-warning-800 ring-1 ring-inset ring-warning-600/20 dark:bg-warning-400/10 dark:text-warning-300">
                                                                    interno — não vai à IA
                                                                </span>
                                                            ) : null}
                                                        </span>
                                                        <span
                                                            className={`block whitespace-pre-wrap text-xs text-mono-600 dark:text-mono-300 ${
                                                                expanded ? '' : 'line-clamp-2'
                                                            }`}
                                                        >
                                                            {product[key]}
                                                        </span>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        ) : targetSku ? (
                            <p className="py-2 text-xs text-mono-400">
                                SKU {targetSku} ainda sem documento de produto.
                                {canManage ? ' Clique em "Documento" para cadastrar — a IA responde com ele.' : ''}
                            </p>
                        ) : (
                            <p className="py-2 text-xs text-mono-400">
                                Item sem SKU vinculado — mostrando só as políticas gerais.
                            </p>
                        )}

                        {/* Fichas livres + políticas gerais */}
                        {entries && entries.length > 0 ? (
                            <div className="mt-1 border-t border-mono-100 pt-1 dark:border-mono-900">
                                <p className="pt-1 text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                                    Fichas e políticas
                                </p>
                                <ul className="divide-y divide-mono-100 dark:divide-mono-900">
                                    {entries.map((entry) => {
                                        const expanded = expandedId === entry.id;
                                        return (
                                            <li key={entry.id} className="py-1.5">
                                                <div className="flex items-start justify-between gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => setExpandedId(expanded ? null : entry.id)}
                                                        className="min-w-0 flex-1 text-left"
                                                        title={expanded ? 'Recolher' : 'Expandir'}
                                                    >
                                                        <span className="flex items-center gap-1.5">
                                                            <span className="truncate text-sm font-medium text-black dark:text-white">
                                                                {entry.title}
                                                            </span>
                                                            {entry.scope === 'general' ? (
                                                                <span className="shrink-0 rounded-full bg-mono-100 px-1.5 text-[10px] font-semibold text-mono-500 dark:bg-mono-900 dark:text-mono-400">
                                                                    política
                                                                </span>
                                                            ) : null}
                                                        </span>
                                                        <span
                                                            className={`block whitespace-pre-wrap text-xs text-mono-500 dark:text-mono-400 ${
                                                                expanded ? '' : 'line-clamp-2'
                                                            }`}
                                                        >
                                                            {entry.content}
                                                        </span>
                                                    </button>
                                                    {canManage ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => onEditFicha(entry)}
                                                            title="Editar ficha"
                                                        >
                                                            <PencilSimple size={14} />
                                                        </Button>
                                                    ) : null}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        </section>
    );
};

const EMPTY_PRODUCT_FORM = {
    sku: '',
    name: '',
    brand: '',
    model: '',
    specs: '',
    package_contents: '',
    compatibility: '',
    warranty_info: '',
    shipping_notes: '',
    usage_notes: '',
    faq: '',
    internal_notes: '',
};

/**
 * Cadastro/edição do DOCUMENTO de produto de um SKU (upsert). `sku` chega do
 * item em atendimento; `product` (se existir) preenche o formulário. Salvo,
 * chama `onSaved(product)` para o painel atualizar.
 */
export function ProductDocumentModal({ isOpen, onClose, sku, product, onSaved }) {
    const [form, setForm] = useState(EMPTY_PRODUCT_FORM);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    // Preenche SÓ na transição fechado→aberto: props mudando com o modal aberto
    // (ex.: contexto que resolve atrasado) não podem apagar o que o operador
    // está digitando.
    const wasOpenRef = useRef(false);

    useEffect(() => {
        if (!isOpen) {
            wasOpenRef.current = false;
            return;
        }
        if (wasOpenRef.current) {
            return;
        }
        wasOpenRef.current = true;
        setFormError('');
        setForm({
            ...EMPTY_PRODUCT_FORM,
            sku: product?.sku || sku || '',
            name: product?.name || '',
            brand: product?.brand || '',
            model: product?.model || '',
            specs: product?.specs || '',
            package_contents: product?.package_contents || '',
            compatibility: product?.compatibility || '',
            warranty_info: product?.warranty_info || '',
            shipping_notes: product?.shipping_notes || '',
            usage_notes: product?.usage_notes || '',
            faq: product?.faq || '',
            internal_notes: product?.internal_notes || '',
        });
    }, [isOpen, sku, product]);

    const setField = (key) => (event) => {
        const value = event.target.value;
        setForm((current) => ({ ...current, [key]: value }));
    };

    const handleSave = async () => {
        const targetSku = form.sku.trim();
        const name = form.name.trim();
        if (!targetSku || !name) {
            setFormError('SKU e nome são obrigatórios.');
            return;
        }
        try {
            setSaving(true);
            setFormError('');
            const payload = { name };
            for (const key of ['brand', 'model']) {
                if (form[key].trim()) {
                    payload[key] = form[key].trim();
                }
            }
            for (const [key] of PRODUCT_FORM_SECTIONS) {
                if (form[key].trim()) {
                    payload[key] = form[key].trim();
                }
            }
            const saved = await api.saveAtendimentoProduct(targetSku, payload);
            await onSaved(saved);
            onClose();
        } catch (error) {
            setFormError(error.message || 'Falha ao salvar o documento do produto.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={product ? 'Editar documento do produto' : 'Cadastrar documento do produto'}
            size="2xl"
            icon={<BookOpen size={20} />}
        >
            <div className="max-h-[65vh] space-y-3 overflow-y-auto pr-1">
                <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-mono-600 dark:text-mono-300">SKU</span>
                        <input
                            type="text"
                            value={form.sku}
                            maxLength={150}
                            disabled={Boolean(product)}
                            onChange={setField('sku')}
                            placeholder="SKU do produto (a chave do documento)"
                            className={`${INPUT_CLASS} font-mono disabled:opacity-60`}
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-mono-600 dark:text-mono-300">
                            Nome do produto
                        </span>
                        <input
                            type="text"
                            value={form.name}
                            maxLength={255}
                            onChange={setField('name')}
                            placeholder="Nome interno / título do produto"
                            className={INPUT_CLASS}
                        />
                    </label>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-mono-600 dark:text-mono-300">Marca</span>
                        <input
                            type="text"
                            value={form.brand}
                            maxLength={120}
                            onChange={setField('brand')}
                            className={INPUT_CLASS}
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-mono-600 dark:text-mono-300">Modelo</span>
                        <input
                            type="text"
                            value={form.model}
                            maxLength={120}
                            onChange={setField('model')}
                            className={INPUT_CLASS}
                        />
                    </label>
                </div>
                {PRODUCT_FORM_SECTIONS.map(([key, label, hint]) => (
                    <label key={key} className="block">
                        <span className="mb-1 flex items-center gap-1.5 text-xs font-medium text-mono-600 dark:text-mono-300">
                            {label}
                            {key === 'internal_notes' ? (
                                <span className="rounded-full bg-warning-50 px-1.5 text-[10px] font-semibold text-warning-800 ring-1 ring-inset ring-warning-600/20 dark:bg-warning-400/10 dark:text-warning-300">
                                    não vai para a IA
                                </span>
                            ) : null}
                        </span>
                        <textarea
                            value={form[key]}
                            rows={key === 'specs' || key === 'faq' ? 4 : 2}
                            maxLength={8000}
                            onChange={setField(key)}
                            placeholder={hint}
                            className={INPUT_CLASS}
                        />
                    </label>
                ))}
                {formError ? <p className="text-sm text-red-600 dark:text-red-400">{formError}</p> : null}
            </div>
            <div className="mt-3 flex justify-end gap-2">
                <Button variant="secondary" onClick={onClose} disabled={saving}>
                    Cancelar
                </Button>
                <Button onClick={handleSave} disabled={saving || !form.sku.trim() || !form.name.trim()}>
                    {saving ? 'Salvando...' : 'Salvar documento'}
                </Button>
            </div>
        </Modal>
    );
}

const EMPTY_KNOWLEDGE = { title: '', content: '', sku: '', item_id: '' };

/**
 * Gestão (CRUD) da base de conhecimento — `atendimento:knowledge:manage`.
 * Autossuficiente: carrega a própria lista (com busca) quando abre. `prefill`
 * ({sku, item_id, entry}) abre direto no formulário — é o caminho "nova ficha
 * para ESTE produto" a partir do detalhe.
 */
export function KnowledgeManager({ isOpen, onClose, onChanged, prefill = null }) {
    const [rows, setRows] = useState(null);
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState(null); // null = lista; {} = novo; {id} = edição
    const [form, setForm] = useState(EMPTY_KNOWLEDGE);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [confirmingId, setConfirmingId] = useState(null);

    const load = useCallback(async (term = '') => {
        try {
            const params = { limit: 100 };
            if (term) {
                params.search = term;
            }
            const data = await api.getAtendimentoKnowledge(params);
            setRows(Array.isArray(data?.results) ? data.results : []);
        } catch {
            setRows([]);
        }
    }, []);

    // Abrir o modal reseta o estado: com prefill vai direto ao formulário.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setFormError('');
        setConfirmingId(null);
        setSearch('');
        if (prefill?.entry) {
            setForm({
                title: prefill.entry.title || '',
                content: prefill.entry.content || '',
                sku: prefill.entry.sku || '',
                item_id: prefill.entry.item_id || '',
            });
            setEditing(prefill.entry);
        } else if (prefill) {
            setForm({ ...EMPTY_KNOWLEDGE, sku: prefill.sku || '', item_id: prefill.item_id || '' });
            setEditing({});
        } else {
            setEditing(null);
        }
        load();
    }, [isOpen, prefill, load]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const timer = setTimeout(() => load(search.trim()), 350);
        return () => clearTimeout(timer);
    }, [isOpen, search, load]);

    const backToList = () => {
        setEditing(null);
        setFormError('');
    };

    const startNew = () => {
        setForm(EMPTY_KNOWLEDGE);
        setEditing({});
        setFormError('');
    };

    const startEdit = (entry) => {
        setForm({
            title: entry.title || '',
            content: entry.content || '',
            sku: entry.sku || '',
            item_id: entry.item_id || '',
        });
        setEditing(entry);
        setFormError('');
    };

    const handleSave = async () => {
        const title = form.title.trim();
        const content = form.content.trim();
        if (!title || !content) {
            setFormError('Título e conteúdo são obrigatórios.');
            return;
        }
        try {
            setSaving(true);
            setFormError('');
            const payload = { title, content };
            if (form.sku.trim()) {
                payload.sku = form.sku.trim();
            }
            if (form.item_id.trim()) {
                payload.item_id = form.item_id.trim();
            }
            if (editing?.id) {
                await api.updateAtendimentoKnowledge(editing.id, payload);
            } else {
                await api.createAtendimentoKnowledge(payload);
            }
            await load(search.trim());
            await onChanged();
            backToList();
        } catch (error) {
            setFormError(error.message || 'Falha ao salvar a ficha.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (entry) => {
        try {
            setSaving(true);
            setFormError('');
            await api.deleteAtendimentoKnowledge(entry.id);
            setConfirmingId(null);
            await load(search.trim());
            await onChanged();
        } catch (error) {
            setFormError(error.message || 'Falha ao excluir a ficha.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Base de conhecimento"
            size="2xl"
            icon={<BookOpen size={20} />}
        >
            {editing ? (
                <div className="space-y-3">
                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-mono-600 dark:text-mono-300">Título</span>
                        <input
                            type="text"
                            value={form.title}
                            maxLength={160}
                            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                            placeholder="Ex.: Voltagem e compatibilidade"
                            className={INPUT_CLASS}
                        />
                    </label>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <label className="block">
                            <span className="mb-1 block text-xs font-medium text-mono-600 dark:text-mono-300">
                                SKU (opcional)
                            </span>
                            <input
                                type="text"
                                value={form.sku}
                                maxLength={150}
                                onChange={(event) => setForm((current) => ({ ...current, sku: event.target.value }))}
                                placeholder="Vale para todo anúncio deste SKU"
                                className={INPUT_CLASS}
                            />
                        </label>
                        <label className="block">
                            <span className="mb-1 block text-xs font-medium text-mono-600 dark:text-mono-300">
                                Anúncio ML (opcional)
                            </span>
                            <input
                                type="text"
                                value={form.item_id}
                                maxLength={40}
                                onChange={(event) =>
                                    setForm((current) => ({ ...current, item_id: event.target.value }))
                                }
                                placeholder="Ex.: MLB1234567890"
                                className={INPUT_CLASS}
                            />
                        </label>
                    </div>
                    <p className="text-xs text-mono-400">
                        Sem SKU e sem anúncio, a ficha vira POLÍTICA GERAL da loja e entra no contexto de todo
                        atendimento (troca, prazo de despacho, garantia padrão…).
                    </p>
                    <label className="block">
                        <span className="mb-1 block text-xs font-medium text-mono-600 dark:text-mono-300">
                            Conteúdo
                        </span>
                        <textarea
                            value={form.content}
                            rows={7}
                            maxLength={KNOWLEDGE_CONTENT_MAX}
                            onChange={(event) => setForm((current) => ({ ...current, content: event.target.value }))}
                            placeholder="Specs, medidas, voltagem, o que acompanha, garantia, dicas de instalação…"
                            className={INPUT_CLASS}
                        />
                        <span className="text-xs text-mono-400">
                            {form.content.length}/{KNOWLEDGE_CONTENT_MAX}
                        </span>
                    </label>
                    {formError ? <p className="text-sm text-red-600 dark:text-red-400">{formError}</p> : null}
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={backToList} disabled={saving}>
                            Cancelar
                        </Button>
                        <Button onClick={handleSave} disabled={saving || !form.title.trim() || !form.content.trim()}>
                            {saving ? 'Salvando...' : 'Salvar ficha'}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <MagnifyingGlass
                                size={15}
                                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-mono-400"
                            />
                            <input
                                type="text"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Buscar por título, conteúdo, SKU ou anúncio"
                                className={`${INPUT_CLASS} pl-9`}
                            />
                        </div>
                        <Button size="sm" onClick={startNew}>
                            <Plus size={16} className="mr-1" />
                            Nova ficha
                        </Button>
                    </div>
                    {formError ? <p className="text-sm text-red-600 dark:text-red-400">{formError}</p> : null}
                    {rows === null ? (
                        <div className="flex justify-center py-6">
                            <LoadingSpinner />
                        </div>
                    ) : rows.length === 0 ? (
                        <p className="py-6 text-center text-sm text-mono-500">
                            Nenhuma ficha ainda. Cadastre o que o atendente (e a IA) precisa saber por produto — e as
                            políticas gerais da loja.
                        </p>
                    ) : (
                        <ul className="max-h-96 divide-y divide-mono-100 overflow-y-auto dark:divide-mono-900">
                            {rows.map((entry) => (
                                <li key={entry.id} className="flex items-start justify-between gap-3 py-3">
                                    <div className="min-w-0">
                                        <p className="flex items-center gap-1.5 text-sm font-medium text-black dark:text-white">
                                            <span className="truncate">{entry.title}</span>
                                            <span className="shrink-0 rounded-full bg-mono-100 px-1.5 text-[10px] font-semibold text-mono-500 dark:bg-mono-900 dark:text-mono-400">
                                                {entry.scope === 'general'
                                                    ? 'política geral'
                                                    : entry.sku || entry.item_id}
                                            </span>
                                        </p>
                                        <p className="mt-0.5 line-clamp-2 text-xs text-mono-400">{entry.content}</p>
                                    </div>
                                    {confirmingId === entry.id ? (
                                        <div className="flex shrink-0 items-center gap-1">
                                            <span className="text-xs text-mono-500">Excluir?</span>
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleDelete(entry)}
                                                disabled={saving}
                                            >
                                                Sim
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => setConfirmingId(null)}
                                                disabled={saving}
                                            >
                                                Não
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="flex shrink-0 items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => startEdit(entry)}
                                                title="Editar"
                                            >
                                                <PencilSimple size={16} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setConfirmingId(entry.id)}
                                                title="Excluir"
                                            >
                                                <Trash size={16} />
                                            </Button>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </Modal>
    );
}
